import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, Textarea, ErrorSummary } from 'nhsuk-react-components';
import { listSymptoms, createSymptom, updateSymptom, deleteSymptom, getSymptomDetails } from '../../actions/symptomActions';
import { SYMPTOM_CREATE_RESET, SYMPTOM_UPDATE_RESET } from '../../constants/symptomConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import Modal from '../../components/modal/Modal.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

export default function SymptomCRUDScreen({ history }) {
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Edit Symptoms";
    }, []);

    useEffect(() => {
        dispatch({ type: SYMPTOM_CREATE_RESET });
        dispatch({ type: SYMPTOM_UPDATE_RESET });
    }, [dispatch]);

    const [symptomListFilter, setsymptomListFilter] = useState({ query: '', page: 1, filter: 'Name' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newSymptom, setNewSymptom] = useState({ name: '', description: '' });
    const [updatedSymptom, setUpdatedSymptom] = useState({});

    const symptomList = useSelector((state) => state.symptomList);
    const { loading, error, symptoms, currentPage, totalPages, totalCount } = symptomList;

    const symptomCreate = useSelector((state) => state.symptomCreate);
    const { success: successCreate, error: symptomCreateErrors, loading: symptomCreateLoading } = symptomCreate;

    const symptomUpdate = useSelector((state) => state.symptomUpdate);
    const { success: successUpdate, loading: symptomUpdateProcessing } = symptomUpdate;

    const symptomDetails = useSelector((state) => state.symptomDetails);
    const { symptom: symptomDet, loading: symptomDetailsLoading } = symptomDetails;

    useEffect(() => {
        dispatch(listSymptoms(symptomListFilter));
    }, [dispatch, symptomListFilter]);

    useEffect(() => {
        if (symptomDet) {
            setUpdatedSymptom({ ...symptomDet });
        }
    }, [symptomDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewSymptom({ name: '', description: '' });
            dispatch(listSymptoms(symptomListFilter));
            dispatch({ type: SYMPTOM_CREATE_RESET });
        }
    }, [successCreate, dispatch, symptomListFilter]);

    const handlePageChange = (newPage) => {
        setsymptomListFilter({ ...symptomListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setsymptomListFilter({ ...symptomListFilter, query: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this symptom?')) {
            dispatch(deleteSymptom(id)).then(() => {
                dispatch(listSymptoms(symptomListFilter));
                if (updatedSymptom?.id === id) {
                    setShowEditModal(false);
                    setUpdatedSymptom({});
                }
            });
        }
    };

    const handleEditSymptom = (id) => {
        dispatch(getSymptomDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedSymptom({});
        dispatch({ type: SYMPTOM_UPDATE_RESET });
    };

    const handleCreateSymptom = () => {
        dispatch(createSymptom(newSymptom));
    };

    const handleUpdateSymptom = () => {
        dispatch(updateSymptom(updatedSymptom.id, updatedSymptom)).then(() => {
            dispatch(getSymptomDetails(updatedSymptom.id));
            dispatch(listSymptoms(symptomListFilter));
        });
    };

    const openCreateModal = () => {
        setNewSymptom({ name: '', description: '' });
        dispatch({ type: SYMPTOM_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="Symptom Management" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <Button
                                    className="nhsuk-button--small nhsuk-u-margin-bottom-4"
                                    onClick={openCreateModal}
                                >
                                    + Create Symptom
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-symptoms"
                                        label="Search symptoms"
                                        name="search-symptoms"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {symptoms && symptoms.length > 0 ? (
                                            symptoms.map((symptom) => (
                                                <Details key={symptom.id || symptom._id}>
                                                    <Details.Summary>
                                                        {symptom.name}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Description:</strong> {symptom.description}
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={symptomDetailsLoading}
                                                                onClick={() => handleEditSymptom(symptom.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(symptom.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: SYMPTOM_CREATE_RESET });
                }}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Create Symptom</h2>
                {symptomCreateErrors && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {symptomCreateErrors.map((error) => (
                                    <li key={error}>{error}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Input
                    id="name-create"
                    label="Name"
                    name="name-create"
                    type="text"
                    onChange={(e) => setNewSymptom({ ...newSymptom, name: e.target.value })}
                    value={newSymptom?.name || ''}
                    required
                />
                <Textarea
                    id="description-create"
                    label="Description"
                    name="description-create"
                    onChange={(e) => setNewSymptom({ ...newSymptom, description: e.target.value })}
                    value={newSymptom?.description || ''}
                />
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button
                        className="nhsuk-button--small"
                        disabled={symptomCreateLoading || symptomUpdateProcessing}
                        onClick={handleCreateSymptom}
                    >
                        Create Symptom
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: SYMPTOM_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Edit Symptom</h2>
                {symptomDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        <Input
                            id="name"
                            label="Name"
                            name="name"
                            type="text"
                            onChange={(e) => setUpdatedSymptom({ ...updatedSymptom, name: e.target.value })}
                            value={updatedSymptom.name ? updatedSymptom.name : ''}
                            required
                        />
                        <Textarea
                            id="description"
                            label="Description"
                            name="description"
                            onChange={(e) => setUpdatedSymptom({ ...updatedSymptom, description: e.target.value })}
                            value={updatedSymptom.description ? updatedSymptom.description : ''}
                        />
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The symptom was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button
                                className="nhsuk-button--small"
                                disabled={symptomUpdateProcessing}
                                onClick={handleUpdateSymptom}
                            >
                                Save changes
                            </Button>
                            <Button
                                secondary
                                className="nhsuk-button--small"
                                onClick={handleCloseEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
